import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plan,
  PlanLicense,
  PlanLicenseQuantitySynchronizationType,
  PlanLicenseType,
  Price,
} from '../../../api/graphql/generated';
import { i18nKeyType } from '../../../i18n';
import { i18nPlanDescription, i18nPlanNameBySku } from '../../plan/plan-utils';
import {
  PlanFeature,
  SubscriptionPlanCardFeatures,
} from '../features/subscription-plan-features';
import { LicenseExplorer } from '../license/license-explorer';
import { useSubscriptionContext } from '../subscription.context';
import {
  PlanBilledBy,
  SubscriptionBillingInterval,
} from '../subscription.types';
import './subscription-plan-card.css';

interface SubscriptionPlanCardProps {
  plan: Plan;
  billingInterval: SubscriptionBillingInterval;
  license?: PlanLicense;
  isInCart?: boolean;
}

const SubscriptionPlanCard = (props: SubscriptionPlanCardProps) => {
  const { plan, billingInterval, license, isInCart } = props;

  const { i18n } = useTranslation();

  const { addToCart, removeFromCart, cart } = useSubscriptionContext();

  const price = useMemo(() => {
    if (license?.priceId) {
      return plan.product?.prices?.find(
        (p) => p.id === license.priceId
      ) as Price;
    } else {
      return plan.product?.prices?.find(
        (p) => p.recurringInterval === billingInterval
      ) as Price;
    }
  }, [plan, billingInterval, license?.priceId]);

  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [isLicenseExplorerOpen, setIsLicenseExplorerOpen] = useState(false);

  const isFixedQuantity = plan.synchronizeQuantityBy !== null;

  const [hasHydratedSelectedEntityIds, setHasHydratedSelectedEntityIds] =
    useState<boolean>(false);

  useEffect(() => {
    // When the cart changes, such as after the user has
    // updated or cancelled their subscription, we need to recalculate
    // the selected ids to reflect the changes in the UI.
    if (isInCart) {
      setHasHydratedSelectedEntityIds(false);
    }
  }, [isInCart]);

  useEffect(() => {
    if (hasHydratedSelectedEntityIds) {
      return;
    }

    // we need to go ahead and select the correct entity ids based on the plan.
    // this needs to happen with entities assigned to an existing license and
    // anything that might be in the cart.

    const existingLicensedEntityIds =
      license?.assignments?.map((a) => a.assignedTo) ?? [];

    const cartItem = cart.items?.find((i) => i.plan.id === plan.id);

    const cartEntityIds = cartItem?.assignedToIds ?? [];

    // get the union of the two arrays
    const mergedEntityIds = Array.from(
      new Set([...existingLicensedEntityIds, ...cartEntityIds])
    );

    setSelectedEntityIds(mergedEntityIds);
    setHasHydratedSelectedEntityIds(true);
  }, [license, cart.items, plan.id, hasHydratedSelectedEntityIds]);

  const quantity = useMemo(() => {
    if (isFixedQuantity) {
      return plan.billableQuantity ?? 0;
    } else {
      return selectedEntityIds?.length ?? 0;
    }
  }, [plan.billableQuantity, isFixedQuantity, selectedEntityIds]);

  const planName = i18nPlanNameBySku(plan.sku);
  const planDescription = i18nPlanDescription(plan.name);

  const displayPriceLabel = i18n.t('common:format_currency', {
    value: {
      amount: price.amount,
      currency: price.currency,
    },
  });

  const billedBy = useMemo(() => getPlanBilledBy(plan), [plan]);
  const billedByLocalizedSingular: string | undefined = billedBy
    ? (i18n.t(`common:${billedBy}`) as string)
    : undefined;

  const billedByLocalizedPlural: string | undefined = billedBy
    ? (i18n.t(`common:${billedBy}s`) as string)
    : undefined;

  const intervalLabel = useMemo(() => {
    const perText = '/';

    const interval = (
      i18n.t(`common:billing_interval_${billingInterval}`) as string
    ).toLowerCase();

    let label = `${perText} ${interval}`;

    if (billedByLocalizedSingular) {
      label = `${perText} ${billedByLocalizedSingular.toLowerCase()} ${label}`;
    }

    return label;
  }, [i18n, billedByLocalizedSingular, billingInterval]);

  const licenseExplorerTitle = useMemo(() => {
    if (billedBy === PlanBilledBy.ROBOT) {
      return i18n.t('common:machine_select_machines');
    }

    return i18n.t('common:assign');
  }, [i18n, billedBy]);

  const selectLicenseAssignments = useCallback(() => {
    if (billedBy === PlanBilledBy.ROBOT) {
      setIsLicenseExplorerOpen(true);
    }
  }, [billedBy]);

  const subscribeActionLabel = useMemo(() => {
    let key: i18nKeyType = 'common:select';

    if (isFixedQuantity) {
      if (isInCart) {
        key = 'common:remove';
      }
    }

    return i18n.t(key);
  }, [i18n, isInCart, isFixedQuantity]);

  const quantityLabel = useMemo(() => {
    if (isFixedQuantity) {
      return i18n.t('plan:plan_for_all_quantity_subtotal_label', {
        itemPlural: billedByLocalizedPlural,
        quantity,
      });
    } else {
      return i18n.t('plan:plan_for_selected_quantity_subtotal_label', {
        itemPlural: billedByLocalizedPlural,
        quantity,
      });
    }
  }, [quantity, isFixedQuantity, billedByLocalizedPlural, i18n]);

  /**
   * Toggles whether the product is in or out of the cart.
   * Only applicable for non-assignable, fixed-quantity products.
   */
  const toggleItemInCart = useCallback(() => {
    if (isInCart) {
      removeFromCart(price.id);
    } else {
      addToCart({ plan, price, quantity });
    }
  }, [isInCart, plan, price, quantity, addToCart, removeFromCart]);

  /**
   * Updates the quantity and assigned entities for the product in the cart.
   * Only applicable for assignable products.
   */
  const updateItemAssignmentsInCart = useCallback(
    (entityIds: string[]) => {
      addToCart({
        plan,
        price,
        quantity: entityIds.length,
        assignedToIds: entityIds,
      });
    },
    [plan, price, addToCart]
  );

  const onCardAction = useCallback(() => {
    if (isFixedQuantity) {
      toggleItemInCart();
    } else {
      selectLicenseAssignments();
    }
  }, [isFixedQuantity, toggleItemInCart, selectLicenseAssignments]);

  const overridePlanFeatures = useMemo<PlanFeature[] | undefined>(() => {
    if (plan.sku === 'B-CARE1') {
      return [
        {
          featureData: {
            highlight: {
              titleKey: 'plan:beacon_care_warranty',
              order: 1,
            },
          },
        },
        {
          featureData: {
            highlight: {
              titleKey: 'plan:beacon_care_priority_service',
              order: 1,
            },
          },
        },
      ];
    }
  }, [plan]);

  return (
    <IonCard className="plan-card">
      <img alt={planName} src={mapPlanSkuToImage(plan.sku)} />
      <IonCardHeader className="plan-card-header">
        <IonCardTitle className="plan-card-header-title">
          {planName}
        </IonCardTitle>
        <IonCardSubtitle className="plan-card-header-subtitle">
          <div dangerouslySetInnerHTML={{ __html: planDescription }} />
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent className="plan-card-content">
        <div className="plan-price-container">
          <span className="price-label">{displayPriceLabel}</span>
          <span className="price-interval">{' ' + intervalLabel}</span>
        </div>
        <div className="plan-features-container">
          <div className="plan-features-header">
            <span>{i18n.t('common:whats_included')}</span>
          </div>
          <div className="plan-features-content">
            <SubscriptionPlanCardFeatures
              plan={plan}
              overrideFeatures={overridePlanFeatures}
            />
          </div>
        </div>
      </IonCardContent>
      <div className="plan-card-subscribe-container">
        <IonItem lines="none">
          <IonLabel>{quantityLabel}</IonLabel>
          <IonButton
            className="plan-card-subscribe-action"
            fill="clear"
            onClick={onCardAction}
          >
            {subscribeActionLabel}
          </IonButton>
        </IonItem>
      </div>
      {billedBy && (
        <LicenseExplorer
          isOpen={isLicenseExplorerOpen}
          title={licenseExplorerTitle}
          autoSelectedEntityIds={selectedEntityIds}
          billedBy={billedBy}
          onClose={() => setIsLicenseExplorerOpen(false)}
          onConfirmation={(entityIds: string[]) => {
            setSelectedEntityIds(entityIds);
            updateItemAssignmentsInCart(entityIds);
            setIsLicenseExplorerOpen(false);
          }}
        />
      )}
    </IonCard>
  );
};

const getPlanBilledBy = (plan: Plan): PlanBilledBy | undefined => {
  let billedBy: PlanBilledBy | undefined;

  if (plan.licenseType === PlanLicenseType.MACHINE) {
    billedBy = PlanBilledBy.ROBOT;
  } else if (plan.synchronizeQuantityBy !== null) {
    switch (plan.synchronizeQuantityBy) {
      case PlanLicenseQuantitySynchronizationType.ACTIVE_MACHINES:
        billedBy = PlanBilledBy.ROBOT;
        break;
    }
  }

  return billedBy;
};

const mapPlanSkuToImage = (sku: string) => {
  switch (sku) {
    case 'B-PRO1':
    case 'B-PRO2':
      return '/assets/pro-plan/pro-plan.png';
    case 'B-CARE1':
      return '/assets/pro-plan/beacon-care.png';
    case 'B-WMP1':
      return '/assets/pro-plan/multipass.png';
    case 'B-WST1':
      return '/assets/pro-plan/seam-tracking.png';
    default:
      return 'https://ionicframework.com/docs/img/demos/card-media.png';
  }
};

export { SubscriptionPlanCard };
